import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useLandlordAgreementTemplates } from "services/hooks/rentals";

const SOURCE_CHANNELS = ["WHATSAPP", "SELF_SERVICE", "ANY"];

const DEFAULT_FORM = {
	title: "",
	code: "LANDLORD_ONBOARDING",
	version: "v1",
	source_channel: "WHATSAPP",
	commission_rate: "4.00",
};

const UploadWizard = () => {
	const navigate = useNavigate();
	const { createTemplate, isSubmitting, actionError } = useLandlordAgreementTemplates();
	const [activeStep, setActiveStep] = useState(0);
	const [form, setForm] = useState(DEFAULT_FORM);
	const [sourcePdf, setSourcePdf] = useState(null);
	const [error, setError] = useState("");

	const steps = ["Template details", "Upload PDF"];

	const handleNext = () => {
		if (activeStep === 0) {
			if (!form.title?.trim() || !form.code?.trim() || !form.version?.trim()) {
				setError("Title, code, and version are required.");
				return;
			}
			setError("");
			setActiveStep(1);
			return;
		}
		handleSubmit();
	};

	const handleSubmit = async () => {
		if (!sourcePdf) {
			setError("Please select the master agreement PDF.");
			return;
		}
		setError("");
		const result = await createTemplate({
			...form,
			commission_rate: String(form.commission_rate),
			source_pdf: sourcePdf,
		});
		if (result.success) {
			const id = result.data?.id;
			if (id) {
				navigate(`/agreement-templates/${id}/edit`, {
					state: { justCreated: true, title: result.data?.title },
				});
			} else {
				navigate("/agreement-templates");
			}
		} else {
			setError(result.message || "Upload failed.");
		}
	};

	return (
		<Box maxWidth={560}>
			<Button
				startIcon={<ArrowBackIcon />}
				component={RouterLink}
				to="/agreement-templates"
				sx={{ mb: 2 }}
			>
				Back to list
			</Button>

			<Typography variant="h4" gutterBottom>
				Upload agreement template
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				After upload you will position placeholders on the PDF in the visual editor.
			</Typography>

			<Stepper activeStep={activeStep} sx={{ mb: 3 }}>
				{steps.map((label) => (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
				))}
			</Stepper>

			{(error || actionError) && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error || actionError}
				</Alert>
			)}

			<Paper sx={{ p: 3 }}>
				{activeStep === 0 && (
					<Box display="flex" flexDirection="column" gap={2}>
						<TextField
							label="Title"
							value={form.title}
							onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
							fullWidth
						/>
						<TextField
							label="Code"
							helperText="e.g. LANDLORD_ONBOARDING"
							value={form.code}
							onChange={(e) =>
								setForm((f) => ({
									...f,
									code: e.target.value.toUpperCase().replace(/\s+/g, "_"),
								}))
							}
							fullWidth
						/>
						<TextField
							label="Version"
							value={form.version}
							onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
							fullWidth
						/>
						<FormControl fullWidth>
							<InputLabel>Source channel</InputLabel>
							<Select
								label="Source channel"
								value={form.source_channel}
								onChange={(e) => setForm((f) => ({ ...f, source_channel: e.target.value }))}
							>
								{SOURCE_CHANNELS.map((ch) => (
									<MenuItem key={ch} value={ch}>
										{ch}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Default commission rate (%)"
							type="number"
							inputProps={{ step: "0.01", min: 0 }}
							value={form.commission_rate}
							onChange={(e) => setForm((f) => ({ ...f, commission_rate: e.target.value }))}
							fullWidth
						/>
					</Box>
				)}

				{activeStep === 1 && (
					<Box>
						<Button
							component="label"
							variant="outlined"
							startIcon={<PictureAsPdfIcon />}
							fullWidth
							sx={{ py: 3 }}
						>
							{sourcePdf ? sourcePdf.name : "Select master agreement PDF"}
							<input
								type="file"
								hidden
								accept="application/pdf,.pdf"
								onChange={(e) => setSourcePdf(e.target.files?.[0] || null)}
							/>
						</Button>
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
							The server applies a starter placeholder layout if none is sent. You will fine-tune
							positions in the next step.
						</Typography>
					</Box>
				)}

				<Box display="flex" justifyContent="space-between" mt={3}>
					<Button disabled={activeStep === 0 || isSubmitting} onClick={() => setActiveStep(0)}>
						Back
					</Button>
					<Button variant="contained" onClick={handleNext} disabled={isSubmitting}>
						{activeStep === 1
							? isSubmitting
								? "Uploading…"
								: "Upload & open editor"
							: "Continue"}
					</Button>
				</Box>
			</Paper>
		</Box>
	);
};

export default UploadWizard;
